import {
  userService,
  type PlatformUserDetailUpdateRequest,
  type PlatformUserResponse,
  type PlatformUserSearchRequest,
} from '@/entities/user/api/user.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const platformUserKeys = {
  all: ['platform-users'] as const,
  lists: () => [...platformUserKeys.all, 'list'] as const,
  list: (search: PlatformUserSearchRequest, pageable: Pageable) =>
    [...platformUserKeys.lists(), search, pageable] as const,
  details: () => [...platformUserKeys.all, 'detail'] as const,
  detail: (userId: number) => [...platformUserKeys.details(), userId] as const,
  loginMethods: (userId: number) =>
    [...platformUserKeys.all, 'login-methods', userId] as const,
};

export interface PlatformUserListParams {
  search: PlatformUserSearchRequest;
  pageable: Pageable;
}

/**
 * 플랫폼 사용자 목록 조회.
 * 레거시 `usersInfo.js` 대응.
 */
export const usePlatformUserList = ({
  search,
  pageable,
}: PlatformUserListParams) =>
  usePaginatedQuery<PlatformUserSearchRequest, PlatformUserResponse>({
    queryKeyPrefix: platformUserKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) => userService.getUsers(s, p),
  });

/**
 * 플랫폼 사용자 상세. 같은 이메일로 탈퇴·재가입한 계정이 여럿일 수 있어
 * 목록 행의 사용자 ID 로 조회한다.
 */
export const usePlatformUserDetail = (userId: number | null) =>
  useQuery({
    queryKey: userId != null ? platformUserKeys.detail(userId) : [],
    queryFn: async () => {
      if (userId == null) throw new Error('userId is required');
      return userService.getUserDetail(userId);
    },
    enabled: userId != null,
  });

/**
 * 로그인 수단 카드. 상세 응답을 받은 뒤 그 userId 로 조회한다.
 * 어드민은 조회만 하므로 변경 뮤테이션이 없다.
 */
export const usePlatformUserLoginMethods = (userId: number | null) =>
  useQuery({
    queryKey: userId != null ? platformUserKeys.loginMethods(userId) : [],
    queryFn: async () => {
      if (userId == null) throw new Error('userId is required');
      return userService.getLoginMethods(userId);
    },
    enabled: userId != null,
  });

export const useUpdatePlatformUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: number;
      data: PlatformUserDetailUpdateRequest;
    }) => userService.updateUserDetail(userId, data),
    onSuccess: () => {
      // 목록만 invalidate. 상세는 화면에서 refetch 1회로 맞춤 (중복 조회·stale hydrate 방지)
      queryClient.invalidateQueries({ queryKey: platformUserKeys.lists() });
    },
  });
};

/**
 * 관리자 탈퇴 처리. 탈퇴 후 로그인 수단 카드는 탈퇴 시점 스냅샷으로 바뀌므로 함께 다시 받는다.
 * 상세는 저장과 같이 화면에서 refetch 결과로 폼을 다시 맞춘다.
 */
export const useWithdrawPlatformUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => userService.withdrawUser(userId),
    onSettled: (_data, _error, userId) => {
      // 404(이미 탈퇴)여도 화면 값이 오래된 것이므로 성공·실패 모두 다시 받는다
      queryClient.invalidateQueries({ queryKey: platformUserKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: platformUserKeys.loginMethods(userId),
      });
    },
  });
};

export const useTrainingPresignedUpload = () =>
  useMutation({
    mutationFn: (filename: string) =>
      userService.generateTrainingVerifiedPresignedUrl({ filename }),
  });

export const usePromoPresignedUpload = () =>
  useMutation({
    mutationFn: (filename: string) =>
      userService.generatePromoLicensePresignedUrl({ filename }),
  });

export const useBusinessLicensePresignedUpload = () =>
  useMutation({
    mutationFn: (filename: string) =>
      userService.generateBusinessLicensePresignedUrl({ filename }),
  });

export const useBankbookPresignedUpload = () =>
  useMutation({
    mutationFn: (filename: string) =>
      userService.generateBankbookPresignedUrl({ filename }),
  });

export const useResetBusinessLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => userService.resetBusinessLicense(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformUserKeys.lists() });
    },
  });
};

export const useResetPromoLicense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => userService.resetPromoLicense(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformUserKeys.lists() });
    },
  });
};

export const useMaxMatchLiteCurrentMonthUsage = (userId: number | null) =>
  useQuery({
    queryKey: [...platformUserKeys.all, 'max-match-lite', userId] as const,
    queryFn: async () => {
      if (userId == null) throw new Error('userId is required');
      return userService.getMaxMatchLiteCurrentMonthUsage(userId);
    },
    enabled: userId != null,
  });

export const useResetMaxMatchLiteUsage = () =>
  useMutation({
    mutationFn: async ({
      userId,
      reason,
    }: {
      userId: number;
      reason: string;
    }) => userService.resetMaxMatchLiteCurrentMonthUsage(userId, reason),
  });

/** 플랫폼 사용자 엑셀 일괄 등록 */
export const useBulkRegisterPlatformUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userService.bulkRegisterUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformUserKeys.lists() });
    },
  });
};
